import Card from "components/card";
import CrispLogo from "assets/img/plugins/crisp.webp";
import GoBack from "components/actions/GoBack";
import InputField from "components/fields/InputField";
import SaveStatus from "components/actions/SaveStatus";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getPlugin, updatePlugin } from "interfaces/plugin";

const CrispPlugin = () => {
    const [saveStatus, setSaveStatus] = useState<any>("none");
    const [plugin, setPlugin] = useState<any>({
        service: "crisp",
        enabled: false,
        events: [],
        secrets: { websiteId: "" },
        variables: { 
            color: "#518dd1",
            position: "right"
        },
    });

    const editPlugin = async () => {
        const res = await updatePlugin("crisp", plugin);
        if (res)
            setSaveStatus("saved");
        else
            setSaveStatus("error");
    };

    useEffect(() => {
        const delayDebounceFn = setTimeout(async () => {
            // Valider le format du Website ID (UUID)
            const websiteId = plugin?.secrets?.websiteId || "";
            const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
            
            if (websiteId === "" || uuidRegex.test(websiteId)) {
                editPlugin();
            } else {
                setSaveStatus("error");
                toast.error("Website ID invalide (format attendu: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)");
            }
        }, 1500);

        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plugin]);

    const initPlugin = async () => {
        const res = await getPlugin("crisp");
        if (res) {
            console.log(res);
            setPlugin({
                ...plugin,
                enabled: res.enabled || false,
                secrets: res.secrets || { websiteId: "" },
                variables: res.variables || { color: "#518dd1", position: "right" }
            });
        }
    };

    useEffect(() => {
        initPlugin();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="">
            <GoBack />
            <Card extra={"relative w-full mt-4 px-2 py-6 border"}>
                {/* Badge */}
                <div className="absolute top-3 right-0">
                    <SaveStatus status={saveStatus} />
                </div>
                {/* Header */}
                <div className="flex w-full px-1">
                    <img
                        src={CrispLogo}
                        alt={`crisp-logo`}
                        className="ml-1 w-[70px] h-[70px] border rounded-lg"
                    />
                    <div className="ml-3 mt-2">
                        <h4 className="text-xl font-medium">Chat</h4>
                        <p className="mt-2 text-xs">
                            Activez le chat client sur votre page de commande B2B.
                        </p>
                    </div>
                </div>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4 mx-1">
                    <p className="text-xs text-gray-700">
                        <strong>Comment obtenir votre Website ID ?</strong>
                        <br />
                        1. Contactez-nous pour ajouter cette fonctionnalité qui n'est pas inclu dans votre abonnement actuel.
                        <br />
                        2. Un membre de notre équipe vous aidera à configurer votre compte.
                        <br />
                        3. Une fois configuré, vous recevrez votre Website ID à entrer ci-dessous.
                    </p>
                </div>

                <InputField
                    id="websiteId"
                    label="Website ID *"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={plugin.secrets.websiteId}
                    extra="mt-4"
                    inputSmall={true}
                    onChange={(e: any) => {
                        setPlugin({
                            ...plugin,
                            secrets: { ...plugin.secrets, websiteId: e.target.value }
                        });
                        setSaveStatus("saving");
                    }}
                />

                {/* Color Picker */}
                <div className="mt-4 px-1">
                    <label className="text-sm text-navy-700 dark:text-white font-bold">
                        Couleur du chat
                    </label>
                    <div className="flex items-center gap-3 mt-2">
                        <input
                            type="color"
                            value={plugin.variables.color}
                            onChange={(e) => {
                                setPlugin({
                                    ...plugin,
                                    variables: { ...plugin.variables, color: e.target.value }
                                });
                                setSaveStatus("saving");
                            }}
                            className="w-12 h-10 rounded cursor-pointer border border-gray-200"
                        />
                        <input
                            type="text"
                            value={plugin.variables.color}
                            onChange={(e) => {
                                setPlugin({
                                    ...plugin,
                                    variables: { ...plugin.variables, color: e.target.value }
                                });
                                setSaveStatus("saving");
                            }}
                            className="flex h-10 w-32 items-center justify-center rounded-xl border bg-white/0 p-3 text-sm outline-none border-gray-200 dark:border-white/10"
                            placeholder="#518dd1"
                        />
                    </div>
                </div>

                {/* Position Radio */}
                <div className="mt-4 px-1">
                    <label className="text-sm text-navy-700 dark:text-white font-bold mb-2 block">
                        Position du chat
                    </label>
                    <div className="flex gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="position"
                                checked={plugin.variables.position === "right"}
                                onChange={() => {
                                    setPlugin({
                                        ...plugin,
                                        variables: { ...plugin.variables, position: "right" }
                                    });
                                    setSaveStatus("saving");
                                }}
                                className="w-4 h-4"
                            />
                            <span className="text-sm">Droite</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="radio"
                                name="position"
                                checked={plugin.variables.position === "left"}
                                onChange={() => {
                                    setPlugin({
                                        ...plugin,
                                        variables: { ...plugin.variables, position: "left" }
                                    });
                                    setSaveStatus("saving");
                                }}
                                className="w-4 h-4"
                            />
                            <span className="text-sm">Gauche</span>
                        </label>
                    </div>
                </div>

                {/* Enable Toggle */}
                <div className="flex items-center gap-3 mt-4 px-1">
                    <input
                        type="checkbox"
                        id="enabled"
                        checked={plugin.enabled}
                        onChange={(e) => {
                            setPlugin({ ...plugin, enabled: e.target.checked });
                            setSaveStatus("saving");
                        }}
                        className="w-5 h-5 rounded cursor-pointer"
                    />
                    <label htmlFor="enabled" className="text-sm font-medium cursor-pointer">
                        Activer le chat sur ma page B2B
                    </label>
                </div>
            </Card>
        </div>
    );
};

export default CrispPlugin;